import React from 'react';
import { LuBanknote, LuFileText, LuMapPin, LuPlus } from 'react-icons/lu';

import { CollapsibleSection } from '@/components/CollapsibleSection';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Button } from '@/components/ui/button';
import { useRequiredParams } from 'utils';

import { AdvancedTaxRates } from './AdvancedTaxRates';
import { EditTaxableIncomeDialog } from './EditTaxableIncomeDialog';
import { EditTaxFilingDialog } from './EditTaxFilingDialog';
import { EditTaxResidencyDialog } from './EditTaxResidencyDialog';
import { TaxableIncomeCard } from './TaxableIncomeCard';
import { TaxFilingCard } from './TaxFilingCard';
import { TaxResidencyCard } from './TaxResidencyCard';
import { useTaxProfile } from './useTaxProfile';

export const TaxProfileTab = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);

    const {
        groupedTaxableIncome,
        groupedTaxFilings,
        groupedTaxResidencies,
        editingIncome,
        isIncomeDialogOpen,
        incomeToDelete,
        setIncomeToDelete,
        editingFiling,
        isFilingDialogOpen,
        filingToDelete,
        setFilingToDelete,
        editingResidency,
        isResidencyDialogOpen,
        residencyToDelete,
        setResidencyToDelete,
        handleAddIncome,
        handleEditIncome,
        handleSaveIncome,
        handleCancelIncomeDialog,
        handleDeleteIncome,
        confirmDeleteIncome,
        handleAddFiling,
        handleEditFiling,
        handleSaveFiling,
        handleCancelFilingDialog,
        handleDeleteFiling,
        confirmDeleteFiling,
        handleAddResidency,
        handleEditResidency,
        handleSaveResidency,
        handleCancelResidencyDialog,
        handleDeleteResidency,
        confirmDeleteResidency,
        canDeleteActiveIncome,
        canDeleteActiveFiling,
        canDeleteActiveResidency,
        deleteIncome,
        deleteFiling,
        deleteResidency,
    } = useTaxProfile(accountGroupGUID);

    const hasActiveItems =
        groupedTaxableIncome.active.length > 0 ||
        groupedTaxFilings.active.length > 0 ||
        groupedTaxResidencies.active.length > 0;
    const hasUpcomingItems =
        groupedTaxableIncome.upcoming.length > 0 ||
        groupedTaxFilings.upcoming.length > 0 ||
        groupedTaxResidencies.upcoming.length > 0;
    const hasExpiredItems =
        groupedTaxableIncome.expired.length > 0 ||
        groupedTaxFilings.expired.length > 0 ||
        groupedTaxResidencies.expired.length > 0;

    const getDeleteModalTitle = () => {
        if (incomeToDelete) return 'Delete Taxable Income';
        if (filingToDelete) return 'Delete Tax Filing';
        return 'Delete Tax Residency';
    };

    const getDeleteModalDescription = () => {
        const item = incomeToDelete || filingToDelete || residencyToDelete;
        if (!item) return '';
        return `Are you sure you want to delete "${item.title.plainText}"? This action cannot be undone.`;
    };

    const getDeleteConfirmHandler = () => {
        if (incomeToDelete) return confirmDeleteIncome;
        if (filingToDelete) return confirmDeleteFiling;
        return confirmDeleteResidency;
    };

    const isDeleteModalOpen = !!incomeToDelete || !!filingToDelete || !!residencyToDelete;
    const isDeleting = deleteIncome.isPending || deleteFiling.isPending || deleteResidency.isPending;

    const TaxProfileTable = () => (
        <div className="space-y-3">
            <div className="mb-3 flex items-center justify-between border-b pb-2">
                <div className="grid w-full grid-cols-3 divide-x divide-gray-200">
                    <div className="flex items-center justify-between pr-4">
                        <div className="flex items-center gap-2">
                            <LuBanknote className="h-5 w-5 text-gray-600" />
                            <h3 className="text-cocoa-1000 font-semibold">Taxable Incomes</h3>
                        </div>
                        <Button variant="ghost" onClick={handleAddIncome}>
                            <LuPlus />
                        </Button>
                    </div>
                    <div className="flex items-center justify-between px-4">
                        <div className="flex items-center gap-2">
                            <LuFileText className="h-5 w-5 text-gray-600" />
                            <h3 className="text-cocoa-1000 font-semibold">Tax Filings</h3>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleAddFiling}>
                            <LuPlus />
                        </Button>
                    </div>
                    <div className="flex items-center justify-between pl-4">
                        <div className="flex items-center gap-2">
                            <LuMapPin className="h-5 w-5 text-gray-600" />
                            <h3 className="text-cocoa-1000 font-semibold">Tax Residencies</h3>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleAddResidency}>
                            <LuPlus />
                        </Button>
                    </div>
                </div>
            </div>

            {hasActiveItems && (
                <CollapsibleSection title="Active">
                    <div className="grid grid-cols-3 divide-x divide-gray-200">
                        <div className="space-y-2 pr-4">
                            {groupedTaxableIncome.active.map((income) => (
                                <TaxableIncomeCard
                                    key={income.taxableIncomeGUID}
                                    income={income}
                                    isActive={true}
                                    showDeleteButton={canDeleteActiveIncome(income)}
                                    onEdit={handleEditIncome}
                                    onDelete={handleDeleteIncome}
                                />
                            ))}
                            {groupedTaxableIncome.active.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No active incomes</p>
                            )}
                        </div>
                        <div className="space-y-2 px-4">
                            {groupedTaxFilings.active.map((filing) => (
                                <TaxFilingCard
                                    key={filing.taxFilingGUID}
                                    filing={filing}
                                    isActive={true}
                                    showDeleteButton={canDeleteActiveFiling(filing)}
                                    onEdit={handleEditFiling}
                                    onDelete={handleDeleteFiling}
                                />
                            ))}
                            {groupedTaxFilings.active.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No active filings</p>
                            )}
                        </div>
                        <div className="space-y-2 pl-4">
                            {groupedTaxResidencies.active.map((residency) => (
                                <TaxResidencyCard
                                    key={residency.taxResidencyGUID}
                                    residency={residency}
                                    isActive={true}
                                    showDeleteButton={canDeleteActiveResidency(residency)}
                                    onEdit={handleEditResidency}
                                    onDelete={handleDeleteResidency}
                                />
                            ))}
                            {groupedTaxResidencies.active.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No active residencies</p>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>
            )}

            {hasUpcomingItems && (
                <CollapsibleSection title="Upcoming" defaultOpen={false}>
                    <div className="grid grid-cols-3 divide-x divide-gray-200">
                        <div className="space-y-2 pr-4">
                            {groupedTaxableIncome.upcoming.map((income) => (
                                <TaxableIncomeCard
                                    key={income.taxableIncomeGUID}
                                    income={income}
                                    isActive={false}
                                    showDeleteButton={canDeleteActiveIncome(income)}
                                    onEdit={handleEditIncome}
                                    onDelete={handleDeleteIncome}
                                />
                            ))}
                            {groupedTaxableIncome.upcoming.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No upcoming incomes</p>
                            )}
                        </div>
                        <div className="space-y-2 px-4">
                            {groupedTaxFilings.upcoming.map((filing) => (
                                <TaxFilingCard
                                    key={filing.taxFilingGUID}
                                    filing={filing}
                                    isActive={false}
                                    showDeleteButton={canDeleteActiveFiling(filing)}
                                    onEdit={handleEditFiling}
                                    onDelete={handleDeleteFiling}
                                />
                            ))}
                            {groupedTaxFilings.upcoming.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No upcoming filings</p>
                            )}
                        </div>
                        <div className="space-y-2 pl-4">
                            {groupedTaxResidencies.upcoming.map((residency) => (
                                <TaxResidencyCard
                                    key={residency.taxResidencyGUID}
                                    residency={residency}
                                    isActive={false}
                                    showDeleteButton={canDeleteActiveResidency(residency)}
                                    onEdit={handleEditResidency}
                                    onDelete={handleDeleteResidency}
                                />
                            ))}
                            {groupedTaxResidencies.upcoming.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No upcoming residencies</p>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>
            )}

            {hasExpiredItems && (
                <CollapsibleSection title="Expired" defaultOpen={false}>
                    <div className="grid grid-cols-3 divide-x divide-gray-200">
                        <div className="space-y-2 pr-4">
                            {groupedTaxableIncome.expired.map((income) => (
                                <TaxableIncomeCard
                                    key={income.taxableIncomeGUID}
                                    income={income}
                                    isActive={false}
                                    showDeleteButton={canDeleteActiveIncome(income)}
                                    onEdit={handleEditIncome}
                                    onDelete={handleDeleteIncome}
                                />
                            ))}
                            {groupedTaxableIncome.expired.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No expired incomes</p>
                            )}
                        </div>
                        <div className="space-y-2 px-4">
                            {groupedTaxFilings.expired.map((filing) => (
                                <TaxFilingCard
                                    key={filing.taxFilingGUID}
                                    filing={filing}
                                    isActive={false}
                                    showDeleteButton={canDeleteActiveFiling(filing)}
                                    onEdit={handleEditFiling}
                                    onDelete={handleDeleteFiling}
                                />
                            ))}
                            {groupedTaxFilings.expired.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No expired filings</p>
                            )}
                        </div>
                        <div className="space-y-2 pl-4">
                            {groupedTaxResidencies.expired.map((residency) => (
                                <TaxResidencyCard
                                    key={residency.taxResidencyGUID}
                                    residency={residency}
                                    isActive={false}
                                    showDeleteButton={canDeleteActiveResidency(residency)}
                                    onEdit={handleEditResidency}
                                    onDelete={handleDeleteResidency}
                                />
                            ))}
                            {groupedTaxResidencies.expired.length === 0 && (
                                <p className="text-center text-sm text-gray-400">No expired residencies</p>
                            )}
                        </div>
                    </div>
                </CollapsibleSection>
            )}

            {!hasActiveItems && !hasUpcomingItems && !hasExpiredItems && (
                <p className="text-center text-sm text-gray-500">No tax profile entries</p>
            )}
        </div>
    );

    return (
        <>
            <div className="space-y-4">
                <h2 className="text-cocoa-1000 text-xl">Tax Profile</h2>

                <div className="grid grid-cols-[1fr_300px] gap-6">
                    <TaxProfileTable />
                    <AdvancedTaxRates />
                </div>
            </div>

            <EditTaxableIncomeDialog
                income={editingIncome}
                onSave={handleSaveIncome}
                onCancel={handleCancelIncomeDialog}
                open={isIncomeDialogOpen}
                isActive={editingIncome ? groupedTaxableIncome.active.includes(editingIncome) : false}
            />

            <EditTaxFilingDialog
                filing={editingFiling}
                onSave={handleSaveFiling}
                onCancel={handleCancelFilingDialog}
                open={isFilingDialogOpen}
                isActive={editingFiling ? groupedTaxFilings.active.includes(editingFiling) : false}
            />

            <EditTaxResidencyDialog
                residency={editingResidency}
                onSave={handleSaveResidency}
                onCancel={handleCancelResidencyDialog}
                open={isResidencyDialogOpen}
                isActive={editingResidency ? groupedTaxResidencies.active.includes(editingResidency) : false}
            />

            <ConfirmModal
                open={isDeleteModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setIncomeToDelete(undefined);
                        setFilingToDelete(undefined);
                        setResidencyToDelete(undefined);
                    }
                }}
                title={getDeleteModalTitle()}
                description={getDeleteModalDescription()}
                confirmText="Delete"
                confirmVariant="destructive"
                onConfirm={getDeleteConfirmHandler()}
                loading={isDeleting}
            />
        </>
    );
};
